
import React, { useState, useEffect } from 'react';
import { ChevronLeft, UserCircle, RefreshCw, Bug, Users, Check, Save, UserMinus, Shield, Loader2, LogOut, X, ExternalLink, Lock, Key, Star, Sparkles, Crown, Circle, Settings, ShieldCheck, Zap, Info } from 'lucide-react';
import { supabase } from '../lib/supabase.ts';
import { UserStats, UIImage, AppView, LeaderboardEntry, UserGlitch, Glitch, MemberTier } from '../types.ts';
import { getAuraData, TIER_CONFIG } from '../constants.tsx';
import { openCustomerPortal } from '../services/stripeService.ts';

interface AccountViewProps {
  user: any;
  userTier: MemberTier;
  profileName: string;
  setProfileName: (name: string) => void;
  avatarUrl: string | null;
  setAvatarUrl: (url: string | null) => void;
  stats: UserStats;
  allHistory: UIImage[];
  notify: (msg: string, type?: any) => void;
  setActiveView: (view: AppView) => void;
  setViewedProfileId: (id: string) => void;
}

export default function AccountView({ 
  user, userTier, profileName, setProfileName, avatarUrl, setAvatarUrl, stats, allHistory, notify, setActiveView, setViewedProfileId
}: AccountViewProps) {
  const [isPickingAvatar, setIsPickingAvatar] = useState(false);
  const [newUsername, setNewUsername] = useState(profileName);
  const [isUpdatingUsername, setIsUpdatingUsername] = useState(false);
  const [isPortalLoading, setIsPortalLoading] = useState(false);
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

  const [following, setFollowing] = useState<(LeaderboardEntry & { followed_at: string })[]>([]);
  const [unlockedGlitches, setUnlockedGlitches] = useState<UserGlitch[]>([]);
  const [allGlitches, setAllGlitches] = useState<Glitch[]>([]);
  const [isLoadingSocial, setIsLoadingSocial] = useState(true);
  const [hoveredGlitch, setHoveredGlitch] = useState<Glitch | null>(null);

  const aura = getAuraData(stats.xp);
  const entitlements = TIER_CONFIG[userTier] || TIER_CONFIG.registered;
  const isPaidUser = userTier !== 'guest' && userTier !== 'registered';

  useEffect(() => {
    async function loadAccountData() {
      if (!user) return;
      setIsLoadingSocial(true);
      try {
        const { data: follows } = await supabase
          .from('follows')
          .select(`created_at, following_id, profiles!following_id (id, username, avatar_url, xp, tier, total_blasts, streak)`)
          .eq('follower_id', user.id);
        
        if (follows) {
          const mapped = follows
            .filter(f => f.profiles)
            .map(f => {
              const p = Array.isArray(f.profiles) ? f.profiles[0] : f.profiles;
              return { ...p, followed_at: f.created_at };
            });
          setFollowing(mapped);
        }

        const [{ data: glitches }, { data: userGlitches }] = await Promise.all([
          supabase.from('glitches').select('*').order('aura_reward', { ascending: true }),
          supabase.from('user_glitches').select('*, glitch:glitches(*)').eq('user_id', user.id)
        ]);

        if (glitches) setAllGlitches(glitches);
        if (userGlitches) setUnlockedGlitches(userGlitches);
      } catch (e) {
        console.error("Hub Sync Error:", e);
      } finally {
        setIsLoadingSocial(false);
      }
    }
    loadAccountData();
  }, [user]);

  const handleUpdateUsername = async () => {
    if (newUsername === profileName) return;
    if (newUsername.length < 3) return notify("NAME TOO SHORT", "error");
    setIsUpdatingUsername(true);
    try {
      const { error } = await supabase.from('profiles').update({ username: newUsername }).eq('id', user.id);
      if (error) throw error;
      setProfileName(newUsername);
      notify("IDENTITY SECURED", "success");
    } catch (e: any) {
      notify(e.message || "SYNC FAILED", "error");
    } finally {
      setIsUpdatingUsername(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (newPassword !== confirmPassword) return notify("PASSWORDS DO NOT MATCH", "error");
    if (newPassword.length < 8) return notify("PASSWORD TOO SHORT", "error");
    setIsUpdatingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      setNewPassword('');
      setConfirmPassword('');
      notify("PASSWORD UPDATED", "success");
    } catch (e: any) {
      notify(e.message.toUpperCase(), "error");
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setActiveView('editor');
    notify("LOGGED OUT", "info");
  };

  const branding = () => {
    switch (userTier) {
      case 'god': return { color: 'text-yellow-400', bg: 'bg-yellow-400', border: 'border-yellow-400', shadow: 'shadow-yellow-400/20', icon: <Crown size={28}/> };
      case 'pro': return { color: 'text-purple-400', bg: 'bg-purple-600', border: 'border-purple-500', shadow: 'shadow-purple-500/20', icon: <Sparkles size={28}/> };
      case 'plus': return { color: 'text-blue-400', bg: 'bg-blue-600', border: 'border-blue-500', shadow: 'shadow-blue-500/20', icon: <Star size={28}/> };
      default: return { color: 'text-zinc-500', bg: 'bg-zinc-800', border: 'border-zinc-700', shadow: 'shadow-none', icon: <Circle size={28}/> };
    }
  };

  const brand = branding();
  const masteryPercent = Math.round((unlockedGlitches.length / (allGlitches.length || 1)) * 100);

  return (
    <div className="max-w-4xl mx-auto space-y-12 animate-in slide-in-from-bottom-8">
      {/* MINIMAL HEADER */}
      <div className="flex items-center gap-6">
        <button onClick={() => setActiveView('editor')} className="p-4 bg-zinc-900 border-2 border-zinc-800 rounded-3xl hover:scale-110 active:scale-95 transition-all shadow-xl">
          <ChevronLeft size={28} />
        </button>
        <h2 className="text-6xl font-bungee text-white uppercase tracking-tighter leading-none">SIGMA HUB</h2>
      </div>

      <div className="grid grid-cols-1 gap-12">
        
        {/* BIG PROFILE SECTION */}
        <section className={`bg-zinc-900 border-4 ${brand.border} rounded-[4rem] p-12 shadow-[0_40px_100px_rgba(0,0,0,0.5)] relative overflow-hidden flex flex-col md:flex-row items-center gap-12`}>
          <div className="relative group">
            <div 
              className={`w-64 h-64 rounded-[4rem] overflow-hidden border-8 ${brand.border} shadow-2xl cursor-pointer transition-all hover:scale-105 active:scale-95 bg-zinc-950 z-10`}
              onClick={() => setIsPickingAvatar(true)}
            >
              {avatarUrl ? <img src={avatarUrl} className="w-full h-full object-cover" alt="Avatar" /> : <div className="w-full h-full bg-zinc-800 flex items-center justify-center text-zinc-700"><UserCircle size={100} /></div>}
              <div className="absolute inset-0 bg-black/70 opacity-0 hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-3">
                <RefreshCw size={48} className="text-white animate-spin-slow" />
                <span className="text-xs font-black uppercase text-white tracking-[0.2em]">Change DNA</span>
              </div>
            </div>
          </div>

          <div className="flex-1 space-y-6 text-center md:text-left">
            <div className="space-y-1">
              <h3 className="text-6xl font-bungee text-white uppercase tracking-tight leading-none">{profileName}</h3>
              <div className={`flex items-center justify-center md:justify-start gap-3 text-3xl font-bungee ${brand.color} uppercase`}>
                {brand.icon} {userTier} TIER
              </div>
            </div>

            <div className="space-y-4 max-w-md">
              <div className="flex justify-between items-end px-1">
                <span className="text-xs font-black text-zinc-500 uppercase tracking-widest">{aura.title}</span>
                <span className="text-xs font-black text-white uppercase">Level {aura.level}</span>
              </div>
              <div className="h-12 bg-zinc-950 rounded-3xl overflow-hidden border-2 border-zinc-800 relative shadow-inner">
                <div className={`h-full bg-gradient-to-r from-indigo-600 to-purple-600 transition-all duration-1000 ease-out`} style={{ width: `${aura.percent}%` }} />
                <div className="absolute inset-0 flex items-center justify-center text-xs font-black text-white tracking-[0.3em] drop-shadow-md">
                  {stats.xp.toLocaleString()} XP
                </div>
              </div>
              <p className="text-[10px] font-black text-zinc-600 uppercase text-center md:text-left tracking-widest">
                {aura.auraRemaining.toLocaleString()} XP to Frequency Shift
              </p>
            </div>
          </div>
        </section>

        {/* GLITCH DNA METER */}
        <section className="bg-zinc-900 border-4 border-zinc-800 rounded-[3.5rem] p-10 space-y-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b-2 border-zinc-800 pb-6">
            <div className="space-y-1">
              <h4 className="font-bungee text-3xl text-indigo-400 uppercase tracking-tighter flex items-center gap-3">
                <Bug size={32}/> GLITCH DNA
              </h4>
              <p className="text-xs font-black text-zinc-500 uppercase tracking-widest">Unlocked Achievement Progress</p>
            </div>
            <div className="bg-indigo-600/10 border-2 border-indigo-500/20 px-6 py-3 rounded-2xl flex flex-col items-center">
              <span className="text-2xl font-bungee text-indigo-400">{masteryPercent}%</span>
              <span className="text-[8px] font-black text-indigo-500 uppercase tracking-widest">Mastery Level</span>
            </div>
          </div>

          <div className="grid grid-cols-5 md:grid-cols-10 gap-4">
            {allGlitches.map((g) => {
              const isUnlocked = unlockedGlitches.some(ug => ug.glitch_id === g.id);
              return (
                <div 
                  key={g.id} 
                  onMouseEnter={() => setHoveredGlitch(g)}
                  onMouseLeave={() => setHoveredGlitch(null)}
                  className={`aspect-square rounded-2xl border-4 flex items-center justify-center transition-all cursor-help ${isUnlocked ? 'bg-indigo-600/20 border-indigo-500 text-indigo-400 shadow-lg scale-110' : 'bg-zinc-950 border-zinc-800 text-zinc-800 opacity-30'}`}
                >
                  <Bug size={24} />
                </div>
              );
            })}
          </div>

          <div className="h-24 bg-zinc-950 rounded-3xl border-2 border-zinc-800 p-6 flex items-center gap-6">
            {hoveredGlitch ? (
              <>
                <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center text-white shrink-0"><Info size={24}/></div>
                <div className="space-y-1">
                  <h5 className="font-bungee text-indigo-400 text-lg uppercase leading-none">{hoveredGlitch.name}</h5>
                  <p className="text-[10px] font-black text-zinc-500 uppercase tracking-wider">{hoveredGlitch.description} — Reward: +{hoveredGlitch.aura_reward} Aura</p>
                </div>
              </>
            ) : (
              <p className="w-full text-center text-xs font-black text-zinc-700 uppercase tracking-widest">Hover over a glitch to scan details</p>
            )}
          </div>
        </section>

        {/* SETTINGS & ACTIONS */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          
          {/* SECURITY & HANDLE */}
          <section className="bg-zinc-900 border-4 border-zinc-800 rounded-[3.5rem] p-10 space-y-8 flex flex-col">
            <h4 className="font-bungee text-2xl text-white uppercase tracking-tighter flex items-center gap-3"><Settings size={24}/> Settings</h4>
            
            <div className="space-y-8 flex-1">
              <div className="space-y-4">
                <label className="text-xs font-black text-zinc-500 uppercase tracking-widest px-1">Update Handle</label>
                <div className="space-y-4">
                  <input 
                    type="text" 
                    value={newUsername} 
                    onChange={(e) => setNewUsername(e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
                    className="w-full h-14 bg-zinc-950 border-2 border-zinc-800 rounded-2xl px-5 font-bungee text-xl text-white outline-none focus:border-indigo-500 transition-all" 
                    placeholder="USERNAME"
                  />
                  <button 
                    onClick={handleUpdateUsername} 
                    disabled={isUpdatingUsername || newUsername === profileName} 
                    className="w-full py-4 bg-indigo-600 rounded-2xl text-white hover:bg-indigo-500 disabled:opacity-30 transition-all shadow-lg flex items-center justify-center gap-3"
                    title="Save Identity"
                  >
                    {isUpdatingUsername ? <Loader2 className="animate-spin" size={24} /> : <Save size={24} />}
                    <span className="font-bungee text-sm uppercase">Save Identity</span>
                  </button>
                </div>
              </div>

              <div className="space-y-4 pt-8 border-t border-zinc-800">
                <label className="text-xs font-black text-zinc-500 uppercase tracking-widest px-1">Security Update</label>
                <div className="space-y-3">
                  <input 
                    type="password" 
                    placeholder="NEW PASSWORD"
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    className="w-full h-14 bg-zinc-950 border-2 border-zinc-800 rounded-2xl px-5 text-sm font-bold text-white outline-none focus:border-indigo-500"
                  />
                  <div className="flex items-center gap-3">
                    <input 
                      type="password" 
                      placeholder="VERIFY PASSWORD"
                      value={confirmPassword}
                      onChange={e => setConfirmPassword(e.target.value)}
                      className="flex-1 h-14 bg-zinc-950 border-2 border-zinc-800 rounded-2xl px-5 text-sm font-bold text-white outline-none focus:border-indigo-500"
                    />
                    <button 
                      onClick={handleUpdatePassword} 
                      disabled={isUpdatingPassword || !newPassword || newPassword !== confirmPassword} 
                      className="h-14 w-14 bg-zinc-800 border-2 border-zinc-700 rounded-2xl text-indigo-400 hover:bg-zinc-700 transition-all flex items-center justify-center shrink-0"
                      title="Update Security"
                    >
                      {isUpdatingPassword ? <Loader2 className="animate-spin" size={24} /> : <Key size={24} />}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* SOCIAL & MANAGEMENT */}
          <section className="bg-zinc-900 border-4 border-zinc-800 rounded-[3.5rem] p-10 space-y-8 flex flex-col">
            <h4 className="font-bungee text-2xl text-blue-500 uppercase tracking-tighter flex items-center gap-3"><Users size={24}/> Social & Perks</h4>
            
            <div className="flex-1 space-y-4">
              <button onClick={() => { setViewedProfileId(user.id); setActiveView('profile'); }} className="w-full py-6 bg-indigo-600/10 text-indigo-400 border-4 border-indigo-500/20 rounded-[2rem] font-bungee text-xl hover:bg-indigo-600/20 transition-all flex items-center justify-center gap-3 shadow-xl">
                <ExternalLink size={24}/> VIEW MY PUBLIC DNA
              </button>
              
              {isPaidUser ? (
                <button onClick={() => openCustomerPortal(user.id)} disabled={isPortalLoading} className="w-full py-6 bg-zinc-800 border-4 border-zinc-700 text-white rounded-[2rem] font-bungee text-xl hover:bg-zinc-700 transition-all flex items-center justify-center gap-3 shadow-xl">
                  {isPortalLoading ? <Loader2 size={24} className="animate-spin" /> : <ShieldCheck size={24}/>} MANAGE BILLING
                </button>
              ) : (
                <button onClick={() => setActiveView('store')} className="w-full py-6 bg-yellow-400 text-black border-4 border-yellow-500 rounded-[2rem] font-bungee text-xl hover:scale-105 transition-all flex items-center justify-center gap-3 shadow-[0_15px_40px_rgba(250,196,21,0.3)]">
                  <Zap size={24}/> UPGRADE TO PREMIUM
                </button>
              )}
            </div>

            <button onClick={handleSignOut} className="mt-8 py-4 bg-red-950/20 text-red-500 border-2 border-red-900/20 rounded-2xl font-bungee text-sm hover:bg-red-900/40 transition-all flex items-center justify-center gap-2">
              <LogOut size={18}/> TERMINATE SESSION
            </button>
          </section>
        </div>
      </div>

      {/* DNA PICKER MODAL */}
      {isPickingAvatar && (
        <div className="fixed inset-0 z-[1000] bg-black/98 backdrop-blur-3xl flex items-center justify-center p-4 animate-in fade-in" onClick={() => setIsPickingAvatar(false)}>
          <div className="bg-zinc-900 border-4 border-indigo-500 rounded-[3.5rem] w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-8 flex justify-between items-center border-b-2 border-zinc-800 bg-zinc-900">
              <div className="space-y-1">
                <h3 className="text-4xl font-bungee text-white uppercase">DNA SEQUENCER</h3>
                <p className="text-xs font-black text-zinc-500 uppercase tracking-widest">Select an image from your historical vault</p>
              </div>
              <button onClick={() => setIsPickingAvatar(false)} className="p-4 bg-zinc-800 border-2 border-zinc-700 rounded-2xl text-zinc-400 hover:text-white transition-all"><X size={28}/></button>
            </div>
            <div className="p-8 overflow-y-auto flex-1 custom-scrollbar bg-zinc-950">
              {allHistory.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                  {allHistory.map(img => (
                    <div 
                      key={img.id} 
                      onClick={() => {
                        supabase.from('profiles').update({ avatar_url: img.url }).eq('id', user.id).then(() => {
                          setAvatarUrl(img.url);
                          setIsPickingAvatar(false);
                          notify("DNA SEQUENCE SAVED", "success");
                        });
                      }} 
                      className="group relative aspect-square rounded-[2.5rem] overflow-hidden border-4 border-zinc-800 cursor-pointer hover:border-indigo-500 transition-all bg-zinc-900"
                    >
                      <img src={img.url} className="w-full h-full object-cover" alt={img.name} />
                      <div className="absolute inset-0 bg-indigo-600/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"><Check size={48} className="text-white" /></div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-20 text-center flex flex-col items-center gap-6 opacity-20"><UserCircle size={100}/><p className="font-bungee text-3xl uppercase">Vault is Empty</p></div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
